import { FormatType } from "../../Alert/Filter/FilterType";
import {
  ConfigurationFormType,
  ConfigurationPageTables,
  ConfigurationPageType,
  ConfigurationTableFilterType,
  RuleTypeType,
} from "../interfaces";

const SETUP_GENERAL_PAGE_CONSTANTS_TAB: ConfigurationPageTables = {
  description: "CONF_DESC_SETUP_GENERAL_SETUP",
  type: ConfigurationPageType.TABLE,
  label: "CONSTANTS",
  path: "constants",
  model: {
    entityName: "name",
    label: "CONSTANTS",
    path: "/api/constants",
    name: "Constants",
    attributes: [
      {
        key: "name",
        order: 1,
        label: "CONSTANT_NAME",
        visible: true,
      },
      {
        key: "value",
        order: 2,
        label: "VALUE",
        visible: true,
      },
      {
        key: "description",
        order: 3,
        label: "DESCRIPTION",
        visible: true,
        rules: [
          {
            column: "name",
            type: RuleTypeType.TRANSLATION,
          },
        ],
        tooltip: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
  },
  options: {
    formFields: [
      {
        key: "name",
        label: "CONSTANT_NAME",
        type: ConfigurationFormType.Input,
      },
      {
        key: "value",
        label: "VALUE",
        type: ConfigurationFormType.Input,
      },
      {
        key: "description",
        label: "DESCRIPTION",
        type: ConfigurationFormType.Input,
      },
    ],
    creatable: false,
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "name",
        flex: "100%",
        readonly: true,
      },
      {
        key: "value",
        flex: "100%",
        required: true,
      },
      {
        key: "description",
        flex: "100%",
        readonly: true,
      },
    ],
    deletable: false,
  },
};

const SETUP_GENERAL_PAGE_OPTIMIZATION_LEVER_TAB: ConfigurationPageTables = {
  description: "CONF_PAGE_DESC_DEFAULT",
  type: ConfigurationPageType.TABLE,
  label: "OPTIMIZATION_LEVER",
  path: "optimization_lever",
  model: {
    entityName: "name",
    label: "OPTIMIZATION_LEVER",
    path: "/api/optimization-levers",
    name: "Optimization lever",
    attributes: [
      {
        key: "name",
        order: 1,
        label: "OPTIMIZATION_NAME",
        visible: true,
      },
      {
        key: "type",
        order: 2,
        label: "TYPE",
        visible: true,
      },
      {
        key: "actionable",
        order: 3,
        label: "ACTIONABLE",
        type: FormatType.BOOLEAN,
        visible: true,
        rules: [
          {
            type: "compare-with",
            value: false,
            operator: "eq",
            options: {
              color: "error.main",
            },
          },
        ],
      },
      {
        key: "available",
        order: 4,
        label: "AVAILABLE",
        type: FormatType.BOOLEAN,
        visible: true,
        rules: [
          {
            type: "compare-with",
            value: false,
            operator: "eq",
            options: {
              color: "error.main",
            },
          },
        ],
      },
      {
        key: "direction",
        order: 5,
        label: "DIRECTION",
        visible: true,
        rules: [
          {
            type: "compare-with",
            value: 0,
            operator: "gt",
            options: {
              text: "DIRECTION_INCREASE",
            },
          },
          {
            type: "compare-with",
            value: 0,
            operator: "lt",
            options: {
              text: "DIRECTION_DECREASE",
            },
          },
          {
            type: "compare-with",
            value: 0,
            operator: "eq",
            options: {
              text: "DIRECTION_STABLE",
            },
          },
        ],
      },
      {
        align: "right",
        key: "level",
        order: 6,
        label: "LEVEL",
        visible: true,
      },
      {
        align: "right",
        key: "order",
        order: 7,
        label: "RANK",
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
  },
  options: {
    formFields: [
      {
        key: "name",
        secondaryKey: "lever_name",
        label: "OPTIMIZATION_NAME",
        readonly: true,
        type: ConfigurationFormType.StaticSelect,
      },
      {
        key: "type",
        secondaryKey: "lever_type",
        label: "TYPE",
        readonly: true,
        type: ConfigurationFormType.StaticSelect,
      },
      {
        key: "actionable",
        label: "ACTIONABLE",
        readonly: true,
        type: ConfigurationFormType.Check,
      },
      {
        key: "available",
        label: "AVAILABLE",
        type: ConfigurationFormType.Check,
      },
      {
        key: "direction",
        label: "DIRECTION",
        readonly: true,
        type: ConfigurationFormType.Input,
      },
      {
        key: "level",
        label: "LEVEL",
        type: ConfigurationFormType.Input,
      },
      {
        inputType: "integer",
        key: "order",
        label: "RANK",
        type: ConfigurationFormType.Input,
      },
    ],
    creatable: false,
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "name",
        flex: "100%",
        required: true,
      },
      {
        key: "type",
        flex: "100%",
        required: true,
      },
      {
        key: "actionable",
        flex: "100%",
        required: true,
      },
      {
        key: "available",
        flex: "100%",
        required: true,
      },
      {
        key: "direction",
        flex: "100%",
        required: true,
      },
      {
        key: "level",
        flex: "100%",
        required: true,
      },
      {
        key: "order",
        flex: "100%",
        required: true,
      },
    ],
    deletable: false,
  },
};

const SETUP_BATCH_PAGE_DECISION_TREE_CONFIGURATION_TAB: ConfigurationPageTables =
  {
    type: ConfigurationPageType.TABLE,
    label: "DECISION_TREE_CONFIGURATION",
    description: "CONF_DESC_GENERAL_SETUP_DECISION_TREE",
    path: "decision-tree-configurations",
    model: {
      entityName: "name",
      label: "DECISION_TREE_CONFIGURATION",
      path: "/api/decision-tree-configurations",
      name: "Decision Tree Configurations",
      attributes: [
        {
          key: "list_test",
          order: 1,
          label: "LIST_TEST",
          visible: true,
        },
        {
          key: "list_results",
          order: 2,
          label: "LIST_RESULTS",
          visible: true,
        },
        {
          align: "right",
          key: "result_code",
          order: 3,
          label: "RESULT_CODE",
          visible: true,
        },
        {
          key: "action.code",
          order: 4,
          label: "ACTIONS",
          visible: true,
        },
        {
          align: "right",
          key: "action.direction",
          order: 6,
          label: "ACTION_DIRECTION",
          visible: true,
        },
        {
          align: "right",
          key: "action.default_value",
          order: 7,
          label: "ACTION_VALUE",
          visible: true,
        },
        {
          key: "sentence_price_mismatch",
          order: 8,
          label: "SENTENCE_PRICE_MISMATCH",
          type: FormatType.BOOLEAN,
          visible: true,
        },
      ],
      filters: [
        {
          type: ConfigurationTableFilterType.Search,
        },
        {
          type: ConfigurationTableFilterType.ResetButton,
        },
      ],
    },
    options: {
      formFields: [
        {
          key: "list_test",
          label: "LIST_TEST",
          type: ConfigurationFormType.Input,
        },
        {
          key: "list_results",
          label: "LIST_RESULTS",
          type: ConfigurationFormType.Input,
        },
        {
          key: "result_code",
          label: "RESULT_CODE",
          type: ConfigurationFormType.Input,
        },
        {
          type: ConfigurationFormType.Select,
          api: "/api/decision-tree-actions/",
          apiParams: {
            page_size: 100,
            ordering: "code",
          },
          key: "action",
          label: "ACTIONS",
          labelKey: "code",
          watchLookup: "action",
        },
      ],
      creatable: false,
      copyable: false,
      editable: true,
      editOptions: [
        {
          key: "list_test",
          flex: "50%",
          readonly: true,
        },
        {
          key: "list_results",
          flex: "50%",
          readonly: true,
        },
        {
          key: "result_code",
          flex: "50%",
          readonly: true,
        },
        {
          key: "action",
          flex: "100%",
        },
      ],
      deletable: false,
    },
  };

const SETUP_BATCH_PAGE_DECISION_TREE_ACTION_TAB: ConfigurationPageTables = {
  type: ConfigurationPageType.TABLE,
  label: "DECISION_TREE_ACTION",
  description: "CONF_DESC_GENERAL_SETUP_DECISION_ACTION",
  path: "decision-tree-actions",
  model: {
    entityName: "name",
    label: "DECISION_TREE_ACTION",
    path: "/api/decision-tree-actions",
    name: "Decision Tree Actions",
    attributes: [
      {
        key: "code",
        order: 1,
        label: "CODE",
        visible: true,
      },
      {
        key: "direction",
        order: 3,
        label: "DIRECTION",
        visible: true,
      },
      {
        key: "default_value",
        order: 4,
        label: "ACTION_VALUE",
        visible: true,
      },
      {
        key: "default_gauge_target",
        order: 5,
        label: "DEFAULT_GAUGE_TARGET",
        visible: true,
      },
      {
        key: "description",
        order: 6,
        label: "DESCRIPTION",
        visible: true,
      },
      {
        align: "right",
        key: "created_at",
        order: 7,
        label: "CREATED_AT",
        type: FormatType.DATE,
        visible: true,
      },
      {
        align: "right",
        key: "updated_at",
        order: 8,
        label: "UPDATED_AT",
        type: FormatType.DATE,
        visible: true,
      },
    ],
    filters: [
      {
        type: ConfigurationTableFilterType.Search,
      },
      {
        type: ConfigurationTableFilterType.ResetButton,
      },
    ],
  },
  options: {
    formFields: [
      {
        key: "code",
        label: "CODE",
        type: ConfigurationFormType.Input,
      },
      {
        key: "direction",
        label: "DIRECTION",
        type: ConfigurationFormType.Input,
      },
      {
        key: "default_value",
        label: "ACTION_VALUE",
        type: ConfigurationFormType.Input,
      },
      {
        key: "default_gauge_target",
        label: "DEFAULT_GAUGE_TARGET",
        type: ConfigurationFormType.Input,
      },
      {
        key: "description",
        label: "DESCRIPTION",
        type: ConfigurationFormType.Input,
      },
    ],
    creatable: true,
    createOptions: [
      {
        key: "code",
        flex: "100%",
        required: true,
      },
      {
        key: "direction",
        flex: "100%",
        required: true,
      },
      {
        key: "default_value",
        flex: "100%",
        required: true,
      },
      {
        key: "default_gauge_target",
        flex: "100%",
      },
      {
        key: "description",
        flex: "100%",
        required: true,
      },
    ],
    copyable: false,
    editable: true,
    editOptions: [
      {
        key: "code",
        flex: "100%",
      },
      {
        key: "direction",
        flex: "100%",
        required: true,
      },
      {
        key: "default_value",
        flex: "100%",
      },
      {
        key: "default_gauge_target",
        flex: "100%",
      },
      {
        key: "description",
        flex: "100%",
      },
    ],
    deletable: true,
  },
};

const SETUP_BATCH_PAGE_DECISION_TREE_ACTION_VALUE_TAB: ConfigurationPageTables =
  {
    type: ConfigurationPageType.TABLE,
    label: "DECISION_TREE_ACTION_VALUES",
    description: "CONF_DESC_GENERAL_SETUP_DECISION_ACTION_VALUES",
    path: "decision-tree-actions-values",
    model: {
      entityName: "name",
      label: "DECISION_TREE_ACTION_VALUES",
      path: "/api/decision-tree-actions-values",
      name: "Decision Tree Actions",
      attributes: [
        {
          key: "property.hebergement_nom_court",
          order: 1,
          label: "PROPERTY",
          visible: true,
        },
        {
          key: "uh_type_nandc.short_name",
          order: 2,
          label: "UHTYPE_NC",
          visible: true,
        },
        {
          key: "optimization_lever.name",
          order: 3,
          label: "OPTIMIZATION_LEVER",
          visible: true,
        },
        {
          key: "action.code",
          order: 4,
          label: "ACTIONS",
          visible: true,
        },
        {
          key: "value",
          order: 5,
          label: "VALUE",
          visible: true,
        },
        {
          key: "gauge_target",
          order: 6,
          label: "DEFAULT_GAUGE_TARGET",
          visible: true,
        },
      ],
      filters: [
        {
          type: ConfigurationTableFilterType.Search,
        },
        {
          type: ConfigurationTableFilterType.ResetButton,
        },
      ],
    },
    options: {
      formFields: [
        {
          type: ConfigurationFormType.Select,
          api: "/api/properties/",
          apiParams: {
            page_size: 100,
            ordering: "hebergement_nom_court",
          },
          key: "property",
          label: "PROPERTY",
          labelKey: "hebergement_nom_court",
        },
        {
          type: ConfigurationFormType.Select,
          api: "/api/uh-types-nandc/",
          apiParams: {
            page_size: 100,
            ordering: "short_name",
          },
          key: "uh_type_nandc",
          label: "UHTYPE_NC",
          labelKey: "short_name",
        },
        {
          type: ConfigurationFormType.Select,
          api: "/api/optimization-levers/",
          apiParams: {
            page_size: 100,
            ordering: "name",
          },
          key: "optimization_lever",
          label: "OPTIMIZATION_LEVER",
          labelKey: "name",
        },
        {
          type: ConfigurationFormType.Select,
          api: "/api/decision-tree-actions/",
          apiParams: {
            page_size: 100,
            ordering: "code",
          },
          key: "action",
          label: "ACTIONS",
          labelKey: "code",
        },
        {
          key: "value",
          label: "VALUE",
          type: ConfigurationFormType.Input,
        },
        {
          key: "gauge_target",
          label: "DEFAULT_GAUGE_TARGET",
          type: ConfigurationFormType.Input,
        },
      ],
      creatable: true,
      createOptions: [
        {
          key: "property",
          flex: "50%",
          required: true,
        },
        {
          key: "uh_type_nandc",
          flex: "50%",
        },
        {
          key: "optimization_lever",
          flex: "50%",
          required: true,
        },
        {
          key: "action",
          flex: "50%",
          required: true,
        },
        {
          key: "value",
          flex: "50%",
          required: true,
        },
        {
          key: "gauge_target",
          flex: "50%",
        },
      ],
      copyable: false,
      editable: true,
      editOptions: [
        {
          key: "property",
          flex: "50%",
          required: true,
        },
        {
          key: "uh_type_nandc",
          flex: "50%",
        },
        {
          key: "optimization_lever",
          flex: "50%",
          required: true,
        },
        {
          key: "action",
          flex: "50%",
          required: true,
        },
        {
          key: "value",
          flex: "50%",
          required: true,
        },
        {
          key: "gauge_target",
          flex: "50%",
        },
      ],
      deletable: true,
    },
  };

export const SETUP_GENERAL_PAGE = {
  type: ConfigurationPageType.TABS,
  count: 0,
  description: "CONF_PAGE_DESC_DEFAULT",
  label: "GENERAL",
  pageLabel: "GENERAL_TITLE_SETUP",
  menuLabel: "GENERAL",
  name: "General",
  path: "general",
  isStaff: true,
  hideInMenu: true,
  tabs: [
    SETUP_GENERAL_PAGE_CONSTANTS_TAB,
    SETUP_GENERAL_PAGE_OPTIMIZATION_LEVER_TAB,
    SETUP_BATCH_PAGE_DECISION_TREE_CONFIGURATION_TAB,
    SETUP_BATCH_PAGE_DECISION_TREE_ACTION_TAB,
    SETUP_BATCH_PAGE_DECISION_TREE_ACTION_VALUE_TAB,
  ],
};
